import { toHtml, toText, escapeHtml } from '../editor/content.js';
import { stripCloze } from '../editor/blanks.js';

/**
 * A card type turns one note (a set of fields) into one or more cards and
 * renders each card. Shape:
 * {
 *   id, name, short?, description,   `short` labels the kind in compact pickers
 *   fields: [{ key, label, kind: 'rich' | 'text' | 'choices', required?, placeholder? }],
 *   ords(note) -> number[]           which cards this note should have
 *   cardLabel(ord) -> string
 *   render(note, ord, ctx) -> { front, back, mode: 'flip'|'type'|'mcq', expected?, choices?, multi? }
 *   validate(fields) -> string | null
 *   checks?: { [fieldKey]: (value, fields) -> string | null }   field-level rules shown inline
 *   summary?(count) -> string                                    short detail on which cards saving makes
 * }
 */
const types = new Map();

export function registerCardType(def) {
  types.set(def.id, def);
}

export function getCardType(id) {
  return types.get(id) ?? fallbackType(id);
}

export function listCardTypes() {
  return [...types.values()];
}

/** Keep only the fields a type defines, so stray keys from another type never get saved. */
export function pickFields(type, fields) {
  const out = {};
  for (const f of getCardType(type).fields) out[f.key] = fields?.[f.key] ?? null;
  return out;
}

const isBlank = (v) => (Array.isArray(v) ? v.length === 0 : !toText(v).trim());

/**
 * Every problem with a note, keyed by field ('_' for note-level rules).
 * An empty object means the note can be saved.
 */
export function noteProblems(type, fields) {
  const def = types.get(type);
  if (!def) return { _: 'Unknown kind of card.' };
  const problems = {};
  for (const f of def.fields) {
    const value = fields?.[f.key];
    if (f.required && f.kind !== 'choices' && isBlank(value)) {
      problems[f.key] = `${f.label} can’t be empty.`;
      continue;
    }
    const msg = def.checks?.[f.key]?.(value, fields);
    if (msg) problems[f.key] = msg;
  }
  if (!Object.keys(problems).length) {
    const msg = def.validate(fields ?? {});
    if (msg) problems._ = msg;
  }
  return problems;
}

/** How many cards a note would produce right now. */
export function cardCount(type, fields) {
  try {
    return getCardType(type).ords({ fields: fields ?? {} }).length;
  } catch {
    return 0;
  }
}

export function sortText(note) {
  const t = getCardType(note.type);
  const first = t.fields[0]?.key;
  return stripCloze(toText(note.fields?.[first])).replace(/\s+/g, ' ').trim();
}

export function searchText(note) {
  return Object.values(note.fields ?? {})
    .map((v) => toText(v))
    .join(' ')
    .toLowerCase();
}

// Unknown types (e.g. created by a newer app version) still display safely.
function fallbackType(id) {
  return {
    id,
    name: `Unknown (${id})`,
    fields: [],
    ords: () => [0],
    cardLabel: () => 'Card',
    validate: () => null,
    render: (note) => {
      const parts = Object.entries(note.fields ?? {}).map(
        ([k, v]) => `<p><strong>${escapeHtml(k)}</strong></p>${toHtml(v)}`,
      );
      return { front: parts[0] ?? '', back: parts.join('<hr>'), mode: 'flip' };
    },
  };
}
